import * as Sentry from '@sentry/react';
import { logger } from './utils/logger';
import { Capacitor } from '@capacitor/core';
import { cookieConsentService } from './services/cookieConsentService';

export async function initSentry() {
  // Only initialize in production or if explicitly enabled
  const isDevelopment = import.meta.env.DEV;
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

  if (!sentryDsn) {
    logger.warn('Sentry DSN not configured. Error tracking disabled.');
    return;
  }

  // Don't initialize in development unless VITE_SENTRY_ENABLED is set
  if (isDevelopment && !import.meta.env.VITE_SENTRY_ENABLED) {
    logger.log('Sentry disabled in development mode');
    return;
  }

  // GDPR Compliance: Check for analytics consent before initializing Sentry
  // Necessary cookies are always allowed, but analytics requires explicit consent
  const hasConsent = cookieConsentService.hasConsent();
  const analyticsAllowed = cookieConsentService.isAllowed('analytics');

  if (!hasConsent || !analyticsAllowed) {
    logger.log('Sentry not initialized - waiting for analytics consent');

    // Subscribe to consent changes and initialize when granted
    cookieConsentService.onChange((data) => {
      if (data && data.preferences.analytics) {
        logger.log('Analytics consent granted - initializing Sentry');
        performSentryInit(sentryDsn);
      }
    });
    return;
  }

  await performSentryInit(sentryDsn);
}

async function performSentryInit(sentryDsn: string) {

  // Prevent double initialization
  if (Sentry.getClient()) {
    logger.log('Sentry already initialized');
    return;
  }

  const isNative = Capacitor.isNativePlatform();

  if (isNative) {
    // Try to initialize Sentry Capacitor for native platforms
    try {
      // Use dynamic string to prevent Vite from analyzing this import
      // This module only exists in native builds, not web builds
      const modulePath = '@sentry/capacitor';
      const SentryCapacitorModule = await import(modulePath as any);
      const { SentryCapacitor } = SentryCapacitorModule as any;
      SentryCapacitor.init(
        {
          dsn: sentryDsn,
          environment: import.meta.env.MODE,
          enabled: true,

          // Capture 100% of errors in production
          tracesSampleRate: 1.0,

          // Set release version from package.json
          release: import.meta.env.VITE_APP_VERSION || '1.0.0',

          // Ignore common non-critical errors
          ignoreErrors: [
            // Browser extensions
            'top.GLOBALS',
            // Random plugins/extensions
            'originalCreateNotification',
            'canvas.contentDocument',
            'MyApp_RemoveAllHighlights',
            // Capacitor specific
            'Push notifications only work on native platforms',
          ],

          // Filter sensitive data
          beforeSend(event: Sentry.ErrorEvent) {
            // Remove any potential PII from error messages
            if (event.message) {
              event.message = event.message.replace(/\b[\w._%+-]+@[\w.-]+\.[A-Z]{2,}\b/gi, '[EMAIL]');
            }
            return event;
          },
        },
        Sentry.init
      );
    } catch (error) {
      logger.warn('Failed to initialize SentryCapacitor, falling back to web Sentry:', error);
      // Fall back to web Sentry
      initWebSentry(sentryDsn);
    }
  } else {
    initWebSentry(sentryDsn);
  }

  logger.log('Sentry initialized for', import.meta.env.MODE, 'environment');
}

function initWebSentry(sentryDsn: string) {
  // Initialize regular Sentry for web
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    enabled: true,

    tracesSampleRate: 1.0,
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Session Replay sampling
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    ignoreErrors: [
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'Push notifications only work on native platforms',
    ],

    beforeSend(event: Sentry.ErrorEvent) {
      if (event.message) {
        event.message = event.message.replace(/\b[\w._%+-]+@[\w.-]+\.[A-Z]{2,}\b/gi, '[EMAIL]');
      }
      return event;
    },
  });
}

// Helper to manually capture exceptions
export function captureException(error: Error, context?: Record<string, unknown>) {
  if (context) {
    Sentry.setContext('custom', context);
  }
  Sentry.captureException(error);
}

// Helper to manually capture messages
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  Sentry.captureMessage(message, level);
}

// Helper to set user context (call after login)
export function setUserContext(userId: string, email?: string) {
  Sentry.setUser({
    id: userId,
    email: email,
  });
}

// Helper to clear user context (call on logout)
export function clearUserContext() {
  Sentry.setUser(null);
}
